import { Address } from '@ton/core'
import { useCallback } from 'react'
import { VoteJettonWallet } from '@voteboxio/core'
import { useAppContext } from '../contexts/App.context'
import {
  BoxDto,
  UserVoteSummaryDto,
  UserVoteSummaryDtoStatusEnum,
} from '../types/generated'
import { useBoxChoices } from './useBoxChoices'

/**
 * Hook for claiming jetton rewards from a winning vote choice
 *
 * Determines if a user can claim rewards and provides a function to execute the claim.
 * Users can claim if:
 * - They have voted on the winning choice
 * - Their reward status is UNCLAIMED
 * - They have wallet votes > 0
 * - The user choice matches the box winner choice index
 */
export const useClaimReward = ({
  box,
  recommendedVoteGas,
}: {
  box: BoxDto
  recommendedVoteGas: string
}) => {
  const { client, sender, wallet } = useAppContext()
  const { winningChoice } = useBoxChoices({ box })

  // Extract user vote summary from the box choice
  // This field is only populated for authenticated users
  const userVoteSummary: UserVoteSummaryDto | null =
    winningChoice?.user_vote_summary ?? null

  // Determine if rewards are claimable based on multiple conditions:
  // - User has vote summary (authenticated and voted)
  // - Status is UNCLAIMED (not already claimed)
  // - User has actual votes in the wallet contract
  // - User choice is the box winning choice
  const isClaimable =
    winningChoice !== null &&
    userVoteSummary !== null &&
    userVoteSummary.status === UserVoteSummaryDtoStatusEnum.UNCLAIMED &&
    Number(userVoteSummary.walletVotes) > 0 &&
    box.winner !== null &&
    box.winner === winningChoice?.choiceIndex

  /**
   * Sends a blockchain transaction to claim jetton rewards
   *
   * Process:
   * 1. Validates wallet connection and claimability
   * 2. Initializes the user's VoteJettonWallet contract from jetton master address
   * 3. Sends ClaimUserRewardRequest message to the contract
   * 4. Pays TON for gas fees
   *
   * The VoteJettonWallet contract will transfer the user's share of jettons
   * based on their vote amount relative to total votes on the winning choice
   */
  const claimReward = useCallback(async () => {
    try {
      if (!client || !sender || !wallet) {
        throw new Error('Wallet not connected')
      }

      if (!isClaimable) {
        throw new Error('Reward is not claimable')
      }
      // Parse blockchain addresses for jetton master and user wallet
      const jettonMasterAddress = Address.parse(
        winningChoice.jettonMasterAddress,
      )
      const walletAddress = Address.parse(wallet)

      // Initialize the user's VoteJettonWallet contract
      // This is a deterministic address derived from the jetton master and user wallet
      const myVoteWalletContract =
        await VoteJettonWallet.VoteJettonWallet.fromInit(
          jettonMasterAddress,
          walletAddress,
        )

      // Open the contract for interaction
      const voteJettonWallet = client.open(myVoteWalletContract)

      // The contract will calculate and transfer the user's jetton share
      await voteJettonWallet.send(
        sender,
        { value: BigInt(recommendedVoteGas) },
        {
          $$type: 'ClaimUserRewardRequest',
          query_id: 0n,
        },
      )

      console.log('Claim reward transaction sent successfully')
    } catch (error) {
      throw new Error(`Failed to claim reward: ${error}`)
    }
  }, [client, sender, wallet, isClaimable, winningChoice, recommendedVoteGas])

  return {
    isClaimable,
    claimReward,
    userVoteSummary,
  }
}
